/**
 * Register an equipment row as a pressure-control component (POST
 * /pressure-control/components), or edit its spec (PATCH /components/:id).
 *
 * Registration is step two: the equipment row already exists (Equipment → New).
 * Manufacturer and model are read from that row, not entered here.
 *
 * ELASTOMERS. Each class has a default from D036 where D036 decides it (rams,
 * annulars and hoses carry elastomers; spools, flanges and carriers do not).
 * Where it does not — ram blocks, choke manifolds, accumulators, stacks, other —
 * there is no default and the reader must say yes or no; the API refuses a
 * blank. The select starts on the class's default and starts empty where there
 * is none, so the default is visible rather than applied behind the reader.
 *
 * PRESSURE is entered in the tenant's unit (`pressureUnit`), and a blank is
 * sent as null — "not stated", never 0.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';
import { DhDialog, DhField, DH_INPUT, DH_SECONDARY, useOpenCount } from '../../pages/downhole/DownholeBits';
import { PC_CLASS_LABEL, pcClassLabel, pcErrorMessage } from '../../lib/pressure-control';
import type { PcComponent, PcComponentWrite, PcMeta } from '../../types/pressure-control';

export interface PcEquipmentOption {
  id: string;
  name: string;
  detail: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Absent: register a new component. Present: edit this one's spec. */
  component?: PcComponent | null;
  meta: PcMeta | null | undefined;
  pressureUnit: string | null | undefined;
  /** Equipment that is not yet a component (create only). */
  equipmentOptions?: PcEquipmentOption[];
  /** Whether the equipment list for the picker has arrived (create only). */
  equipmentState?: 'loading' | 'error' | 'ready';
  /** Registered stack assemblies, for "sits in stack". */
  stacks: Array<{ equipmentId: string; name: string }>;
  pending: boolean;
  onSubmit: (body: PcComponentWrite) => Promise<unknown>;
}

const yesNo = (v: boolean | null | undefined) => (v === true ? 'yes' : v === false ? 'no' : '');

export function PcSpecDialog({ open, onClose, component, meta, pressureUnit, equipmentOptions = [], equipmentState = 'ready', stacks, pending, onSubmit }: Props) {
  const editing = !!component;
  const openCount = useOpenCount(open);
  const classes = useMemo(
    () => meta?.componentClasses ?? Object.keys(PC_CLASS_LABEL).map((key) => ({ key, defaultHasElastomers: null })),
    [meta],
  );
  const defaultFor = (cls: string) => classes.find((c) => c.key === cls)?.defaultHasElastomers ?? null;

  const [equipmentId, setEquipmentId] = useState('');
  const [cls, setCls] = useState('');
  const [elastomers, setElastomers] = useState('');
  const [bore, setBore] = useState('');
  const [pressure, setPressure] = useState('');
  const [nace, setNace] = useState(false);
  const [parent, setParent] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const s = component?.spec;
    setEquipmentId('');
    setCls(s?.componentClass ?? '');
    setElastomers(s ? yesNo(s.hasElastomers) : '');
    setBore(s?.boreSize ?? '');
    setPressure(s?.workingPressure === null || s?.workingPressure === undefined ? '' : String(s.workingPressure));
    setNace(!!s?.naceMr0175);
    setParent(s?.stackParentId ?? '');
    setNotes(s?.notes ?? '');
    setError(null);
    // Reset only when the dialog opens — not on a refetch of the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);

  const pickClass = (next: string) => {
    setCls(next);
    if (!editing) setElastomers(yesNo(defaultFor(next)));
    if (next === 'stack_assembly') setParent('');
  };

  const submit = async () => {
    setError(null);
    if (!editing && !equipmentId) return setError('Choose the equipment to register.');
    if (!cls) return setError('Choose the component class.');
    if (elastomers === '') return setError(`Say whether this ${pcClassLabel(cls).toLowerCase()} has elastomers.`);
    let wp: number | null = null;
    if (pressure.trim() !== '') {
      wp = Number(pressure);
      if (!Number.isFinite(wp) || wp <= 0) return setError('Working pressure must be a number greater than 0, or blank.');
    }
    const body: PcComponentWrite = {
      component_class: cls,
      has_elastomers: elastomers === 'yes',
      bore_size: bore.trim() || null,
      working_pressure: wp,
      nace_mr0175: nace,
      stack_parent_id: cls === 'stack_assembly' ? null : parent || null,
      notes: notes.trim() || null,
    };
    if (!editing) body.equipment_id = equipmentId;
    try {
      await onSubmit(body);
      onClose();
    } catch (e) {
      setError(pcErrorMessage(e, editing ? 'The spec could not be saved.' : 'The component could not be registered.'));
    }
  };

  const stackOptions = stacks.filter((s) => s.equipmentId !== component?.equipmentId);

  return (
    <DhDialog
      open={open}
      onClose={onClose}
      title={editing ? `Edit spec — ${component?.name ?? '—'}` : 'Register a pressure-control component'}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={pending}
            className="fi-action fi-action--standard fi-action--primary"
          >
            {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {editing ? 'Save spec' : 'Register'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {!editing && (
          <div className="sm:col-span-2">
            <span className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Equipment</span>
            {equipmentState === 'loading' ? (
              <p className="text-sm text-fieldiq-muted">Loading equipment…</p>
            ) : equipmentState === 'error' ? (
              <p role="alert" className="text-sm text-fieldiq-text">The equipment list could not be loaded. Close this and try again.</p>
            ) : equipmentOptions.length === 0 ? (
              <p className="text-sm text-fieldiq-text" data-testid="pc-no-equipment">
                Every equipment row is already registered, or there is none. Add the unit under Equipment → New first.
              </p>
            ) : (
              <SearchableSelect
                label="Equipment"
                placeholder="Choose a unit"
                options={equipmentOptions.map((o) => ({ value: o.id, label: o.name, detail: o.detail }))}
                value={equipmentId}
                onChange={(v) => setEquipmentId(String(v))}
              />
            )}
          </div>
        )}
        <DhField label="Component class">
          <select className={DH_INPUT} value={cls} onChange={(e) => pickClass(e.target.value)} aria-label="Component class">
            <option value="">Choose…</option>
            {classes.map((c) => (
              <option key={c.key} value={c.key}>{pcClassLabel(c.key)}</option>
            ))}
          </select>
        </DhField>
        <DhField
          label="Has elastomers"
          hint={cls && defaultFor(cls) === null ? 'No default for this class — say which.' : 'Elastomers cap the certificate at the total-life limit.'}
        >
          <select className={DH_INPUT} value={elastomers} onChange={(e) => setElastomers(e.target.value)} aria-label="Has elastomers">
            <option value="">Choose…</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </DhField>
        <DhField label="Bore size">
          <input className={DH_INPUT} value={bore} maxLength={30} onChange={(e) => setBore(e.target.value)} placeholder={'e.g. 7-1/16"'} />
        </DhField>
        <DhField label={`Working pressure (${pressureUnit || '—'})`}>
          <input className={DH_INPUT} inputMode="decimal" value={pressure} onChange={(e) => setPressure(e.target.value)} />
        </DhField>
        {cls !== 'stack_assembly' && (
          <DhField label="Sits in stack" hint="A registered stack assembly, or none.">
            <select className={DH_INPUT} value={parent} onChange={(e) => setParent(e.target.value)} aria-label="Sits in stack">
              <option value="">None</option>
              {stackOptions.map((s) => (
                <option key={s.equipmentId} value={s.equipmentId}>{s.name}</option>
              ))}
            </select>
          </DhField>
        )}
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-fieldiq-text">
          <input type="checkbox" checked={nace} onChange={(e) => setNace(e.target.checked)} />
          NACE MR0175 (sour service)
        </label>
        <div className="sm:col-span-2">
          <DhField label="Notes">
            <textarea className={`${DH_INPUT} h-20 py-2`} value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} />
          </DhField>
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-4 rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
          {error}
        </p>
      )}
    </DhDialog>
  );
}
